<template>
  <div
    v-show="recommendedProducts?.length"
    v-style:bg="backgroundColor"
  >
    <div
      class="overflow-hidden"
      :class="[brandClasses.container, { container: !isFullWidth }]"
    >
      <h2
        v-if="content?.title"
        v-style:c="titleColor"
        class="mb-4 lg:mx-9 lg:mb-6"
        :class="titleStyle"
      >
        <span v-if="showTitleHtml" v-html="titleHtml" />
        <span v-else>{{ title }}</span>
      </h2>
      <client-only>
        <vf-carousel
          v-if="recommendedProducts?.length"
          class="-mx-2"
          :class="brandClasses.carouselContainer"
          :class-controls="['-mt-9 mx-2', brandClasses.carouselControls]"
          loop
        >
          <div
            v-for="(product, i) in recommendedProducts"
            :key="i"
            :class="[variantStyleMap[variant!], brandClasses.productContainer]"
            @click="handleProductClick(product, i + 1)"
          >
            <product-card-recommended
              :currency="currency[locale]"
              v-bind="product"
              :gtm-config="{ list: _list, category: _list }"
              :quickshop
            />
          </div>
        </vf-carousel>
        <template #fallback>
          <div
            class="grid gap-4 children:skeleton lg:px-9"
            :class="variantStyleMap[variant!]"
          >
            <template v-for="i in variant === '3-cards' ? 1 : 2" :key="i">
              <div class="aspect-square" />
              <div class="aspect-square <md:hidden" />
              <div class="aspect-square <lg:hidden" />
            </template>
          </div>
        </template>
      </client-only>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { BloomreachRecommendations } from '#types/components/cms/bloomreach-recommendations'
import { getPageInfo } from '#commerce/gtm/page'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { extractBloomReachProduct } from '#core/utils/p13n'

const { content } = defineProps<{
  content: BloomreachRecommendations
}>()

const { brandClasses, enableQuickShop } = useAppConfig().components.cms.productRecommendations
const { currency } = useRuntimeConfig().public
const locale = useLocale()
const route = useRoute()
const { handleBloomreachProductClick } = useMonetate()
const { showTitleHtml } = useFeatureFlags()
const { $gtm } = useNuxtApp()
const [pageCategory] = getPageInfo(route, {})

const {
  backgroundColor,
  bloomreachIndex,
  ctaStyle,
  isFullWidth,
  originalPriceColor,
  quickShopVisibility,
  salePriceColor,
  showRatings,
  cardTextColor: textColor,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  title,
  titleColor,
  titleHtml,
  titleStyle = 'title-2',
  variant,
  whiteBackground,
} = content

const quickshop = quickShopVisibility ? quickShopVisibility === 'visible' : enableQuickShop

const getRecommendedProducts = (products = []) =>
  products.map((product) => extractBloomReachProduct(product, showRatings))

const recommendedProducts = ref<any>([])

const loadProducts = () => {
  const products = window.vfbr?.[bloomreachIndex || 0] || []
  recommendedProducts.value = getRecommendedProducts(products)
}

const _list = getRecommendationListDescription({ list: `${pageCategory || 'Home'} | Product Recommendation Carousel`, title })

const handleProductClick = (product, position: number) => {
  handleBloomreachProductClick(product, position, title, route.path)
}

const variantStyleMap: Record<NonNullable<BloomreachRecommendations['variant']>, string> = {
  '3-cards': 'w-full md:w-1/2 lg:w-1/3',
  '5-cards': 'w-1/2 md:w-1/4 lg:w-1/5',
  '6-cards': 'w-1/2 md:w-1/4 lg:w-1/6',
}

watchOnce(() => recommendedProducts.value?.length, () => {
  const productItems = recommendedProducts.value.map((product) => ({
    ...product,
    price: +product.price.highOriginal,
    salePrice: +product.price.highCurrent
  }))

  $gtm.push('product.onProductRecImpressions', productItems, {
    currency: currency[locale],
    list: `${pageCategory || 'Home'} | Product Recommendation Carousel`,
    title
  })
})

provide(RecommendationStyleContextKey, {
  whiteBackground,
  textColor,
  originalPriceColor,
  salePriceColor,
  ctaStyle,
  textLinkStyle,
  textLinkColor,
  textLinkHoverColor,
  textLinkUnderline,
})

onMounted(() => {
  loadProducts()
  window.addEventListener('recalculateBloomreachVfa', loadProducts)
})

onBeforeUnmount(() => {
  window.removeEventListener('recalculateBloomreachVfa', loadProducts)
})
</script>
